import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./HotelOwners.css";

/** Backend base URL */
const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ---------------- tiny, dependency-free number animation ---------------- */
function CountUp({ to = 0, duration = 900, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  const startTs = useRef(null);
  const target = Number(to || 0);

  useEffect(() => {
    startTs.current = null;
    let raf = 0;

    const step = (ts) => {
      if (!startTs.current) startTs.current = ts;
      const p = Math.min(1, (ts - startTs.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  const formatted = Math.round(val).toLocaleString();
  return <>{prefix}{formatted}{suffix}</>;
}

// Status component reused for table and button styling
export function Status({ status, small = false }) {
  const isPending = status === "Pending" || status === "Pending Approval";
  const isActive = status === "Active" || status === "Approved";
  const color = isActive ? "#047857" : isPending ? "#b45309" : "#b91c1c";
  const bg = isActive ? "#ecfdf5" : isPending ? "#fffbeb" : "#fef2f2";
  const border = isActive ? "#a7f3d0" : isPending ? "#fde68a" : "#fecaca";

  const style = small
    ? { padding: "4px 8px", fontSize: 12, borderRadius: 12, display: "inline-block", color, background: bg, border: `1px solid ${border}`, fontWeight: 600 }
    : { padding: "6px 12px", borderRadius: 14, display: "inline-block", color, background: bg, border: `1px solid ${border}`, fontWeight: 600 };

  return (
    <span style={{ ...style }}>
      {isPending ? "Pending Approval" : status}
    </span>
  );
}

export default function HotelOwners() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ activeOwners: 0, pendingOwners: 0, inactiveOwners: 0, activeHotels: 0 });
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    businessType: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [businessTypes, setBusinessTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const cards = useMemo(
    () => [
      { title: "Active Hotel Owners", value: stats.activeOwners, sub: "Active Hotel Owners", icon: "🟢", prefix: "+" },
      { title: "Pending Approvals", value: stats.pendingOwners, sub: "Awaiting Verification", icon: "⏳", prefix: "+" },
      { title: "Inactive Owners", value: stats.inactiveOwners, sub: "Inactive Hotel Owners", icon: "🔴", prefix: "+" },
      { title: "Active Hotels", value: stats.activeHotels, sub: "Active Hotels", icon: "🏨", prefix: "+" },
    ],
    [stats]
  );

  useEffect(() => {
    async function fetchFilters() {
      try {
        const headers = getAuthHeaders();
        const [btRes, stRes] = await Promise.all([
          fetch(`${API}/property_types`, { headers }),
          fetch(`${API}/statuses`, { headers })
        ]);
        const btData = await btRes.json();
        const stData = await stRes.json();
        if (Array.isArray(btData)) setBusinessTypes(btData);
        if (Array.isArray(stData)) {
          const uniqueMap = new Map();
          stData.forEach(s => {
            if (s && s.name && !uniqueMap.has(s.name.toLowerCase())) {
              uniqueMap.set(s.name.toLowerCase(), s);
            }
          });
          setStatuses(Array.from(uniqueMap.values()));
        }
      } catch (e) {
        console.error("Failed to fetch filter options", e);
      }
    }
    fetchFilters();
  }, []);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const query = new URLSearchParams(filters).toString();
        const headers = getAuthHeaders();

        const [summaryRes, listRes] = await Promise.all([
          fetch(`${API}/admin/hotel-owners/summary`, { headers }),
          fetch(`${API}/admin/hotel-owners?${query}`, { headers }),
        ]);

        const summary = await summaryRes.json();
        const list = await listRes.json();

        if (!summaryRes.ok) throw new Error(summary?.message || "Failed summary");
        if (!listRes.ok) throw new Error(list?.message || "Failed list");

        if (!mounted) return;
        setStats({
          activeOwners: Number(summary.activeOwners) || 0,
          pendingOwners: Number(summary.pendingOwners) || 0,
          inactiveOwners: Number(summary.inactiveOwners) || 0,
          activeHotels: Number(summary.activeHotels) || 0,
        });

        // Normalize rows: ensure is_active boolean and status string exist
        setRows(
          (Array.isArray(list) ? list : []).map((r) => ({
            ...r,
            dateText: r.date ? new Date(r.date).toLocaleString() : "—",
            is_active: r.is_active === true || r.is_active === 1,
            status: r.status ? String(r.status) : (r.is_active === 1 || r.is_active === true ? "Active" : "Inactive"),
          }))
        );
      } catch (e) {
        if (mounted) setError(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [filters]);

  async function toggleStatus(ownerId) {
    try {
      // optimistic UI for status (Hold <-> Unhold)
      setRows((prev) =>
        prev.map((r) =>
          r.ownerId === ownerId ? { ...r, status: r.status === "Active" ? "Inactive" : "Active" } : r
        )
      );

      const headers = getAuthHeaders();
      const res = await fetch(`${API}/admin/hotel-owners/${ownerId}/status`, {
        method: "PATCH",
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update status.");

      // update precise row + refresh summary
      setRows((prev) =>
        prev.map((r) =>
          r.ownerId === ownerId
            ? {
              ...r,
              status: data.row.status,
              is_active: data.row.is_active,
              date: data.row.date,
              dateText: data.row.date ? new Date(data.row.date).toLocaleString() : "—",
              name: data.row.name,
              location: data.row.location,
            }
            : r
        )
      );
      setStats({
        activeOwners: Number(data.summary.activeOwners) || 0,
        pendingOwners: Number(data.summary.pendingOwners) || 0,
        inactiveOwners: Number(data.summary.inactiveOwners) || 0,
        activeHotels: Number(data.summary.activeHotels) || 0,
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Failed to update status.");
      // reload list on error
      try {
        const listRes = await fetch(`${API}/admin/hotel-owners`, { headers: getAuthHeaders() });
        const list = await listRes.json();
        if (listRes.ok) {
          setRows(
            (Array.isArray(list) ? list : []).map((r) => ({
              ...r,
              dateText: r.date ? new Date(r.date).toLocaleString() : "—",
            }))
          );
        }
      } catch { }
    }
  }

  async function toggleActive(ownerId) {
    try {
      setRows((prev) =>
        prev.map((r) =>
          r.ownerId === ownerId ? { ...r, userExists: !r.userExists } : r
        )
      );

      const headers = getAuthHeaders();
      const res = await fetch(`${API}/admin/hotel-owners/${ownerId}/active`, {
        method: "PATCH",
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update active flag.");

      setRows((prev) =>
        prev.map((r) =>
          r.ownerId === ownerId
            ? { ...r, userExists: data.row.userExists }
            : r
        )
      );
      setStats({
        activeOwners: Number(data.summary.activeOwners) || 0,
        pendingOwners: Number(data.summary.pendingOwners) || 0,
        inactiveOwners: Number(data.summary.inactiveOwners) || 0,
        activeHotels: Number(data.summary.activeHotels) || 0,
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Failed to update active flag.");
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      businessType: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
  };

  if (error) {
    return (
      <div className="owners-page">
        <div className="owners-head">
          <div>
            <h2 className="owners-title">Hotel Owners</h2>
            <p className="muted">An error occurred</p>
          </div>
        </div>
        <section className="card table-card" style={{ padding: 24, color: "crimson" }}>
          {error}
        </section>
      </div>
    );
  }

  return (
    <div className="owners-page">
      {/* Page header */}
      <div className="owners-head">
        <div>
          <h2 className="owners-title">Hotel Owners</h2>
          <p className="muted">Manage all hotel owners</p>
        </div>
      </div>

      {/* Stat cards with animated values */}
      <section className="owners-cards">
        {cards.map((s, i) => (
          <div key={i} className="card stat">
            <div className="stat-title">{s.title}</div>
            <div className="stat-value">
              <CountUp to={s.value} prefix={s.prefix || ""} />
            </div>
            <div className="stat-sub">{s.sub}</div>
            <div className="lock" aria-hidden>{s.icon}</div>
          </div>
        ))}
      </section>

      <div className="filters">
        <input type="text" name="search" placeholder="Search by ID, Name, Location" value={filters.search} onChange={handleFilterChange} />
        <select name="businessType" value={filters.businessType} onChange={handleFilterChange} className={`input ${filters.businessType ? "filter-selected" : "filter-muted"}`} title="Business type">
          <option value="">All Business Types</option>
          {businessTypes.map(bt => <option key={bt.id} value={bt.name}>{bt.name}</option>)}
        </select>
        <select name="status" value={filters.status} onChange={handleFilterChange} className={`input ${filters.status ? "filter-selected" : "filter-muted"}`}>
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
        <input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} className={`input ${filters.dateFrom ? "filter-selected" : "filter-muted"}`} />
        <input type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} className={`input ${filters.dateTo ? "filter-selected" : "filter-muted"}`} />
        <button onClick={handleClearFilters}>Clear All</button>
      </div>

      <h3 className="table-title">All owners</h3>

      {/* Table */}
      <section className={`card table-card ${loading ? 'loading' : ''}`}>
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                <input type="checkbox" aria-label="select all" />
              </th>
              <th>Registered ID</th>
              <th>Business Name</th>
              <th>Business Type</th>
              <th>Location</th>
              <th>Date of Registered</th>
              <th>Status</th>
              <th>Remove Account</th>
              <th style={{ width: 140 }}>Hold Account</th>
              <th style={{ width: 40 }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.ownerId}>
                <td>
                  <input type="checkbox" aria-label={`select ${r.regId}`} />
                </td>
                <td><span className="id-badge">{r.regId}</span></td>
                <td>{r.name}</td>
                <td className="muted">{r.type}</td>
                <td className="muted">{r.location}</td>
                <td className="muted nowrap">{r.dateText}</td>

                {/* Status column uses Status component */}
                <td>
                  <Status status={r.status} />
                </td>

                <td>
                  <button
                    className="badge"
                    onClick={() => toggleActive(r.ownerId)}
                    title={r.userExists ? "Remove account (prevent login)" : "Admit account (restore login)"}
                    style={{
                      color: r.userExists ? "#f97316" : "#047857",
                      background: r.userExists ? "#fff7ed" : "#ecfdf5",
                    }}
                  >
                    {r.userExists ? "Remove" : "Admit"}
                  </button>
                </td>
                <td>
                  {r.status === "Pending" ? (
                    <button
                      className="badge"
                      onClick={() => navigate(`/admin/hotel-owners/${r.ownerId}`)}
                      title="Review application documents and details"
                      style={{
                        color: "#b45309",
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                    >
                      Review
                    </button>
                  ) : (
                    <button
                      className="badge"
                      onClick={() => toggleStatus(r.ownerId)}
                      title={r.status === "Active" ? "Hold (preventing listing)" : "Unhold (allow listing)"}
                      style={{
                        color: r.status === "Active" ? "#f97316" : "#047857",
                        background: r.status === "Active" ? "#fff7ed" : "#ecfdf5",
                        cursor: "pointer"
                      }}
                    >
                      {r.status === "Active" ? "Hold" : "Unhold"}
                    </button>
                  )}
                </td>
                <td className="dots" onClick={() => navigate(`/admin/hotel-owners/${r.ownerId}`)}>
                  <button className="badge gray" style={{ cursor: "pointer" }}>View Details</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}